import * as a from '@mitranim/js/all.mjs'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as dg from '@mitranim/js/dom_global'
import * as u from './util.mjs'
import {E} from './util.mjs'
import * as c from './conf.mjs'
import * as n from './net.mjs'
import * as d from './dat.mjs'
import * as em from './elem-misc.mjs'
import * as efft from './elem-form-field-text.mjs'
import * as effce from './elem-form-field-cents.mjs'
import * as effch from './elem-form-field-checkbox.mjs'
import * as effss from './elem-form-field-select-single.mjs'
import * as effsm from './elem-form-field-select-multi.mjs'

export class Form extends em.MixExecable(u.MixElem(dg.global.HTMLFormElement)) {
  connectedCallback() {
    // SSR disables form controls such as inputs and buttons.
    // We must enable them on the client.
    u.formEnable(this)

    this.onsubmit = this.onSubmit
    this.oninput = this.onInput
    this.onchange = this.onChange
  }

  onSubmit(eve) {this.exec(eve)}
  onInput() {this.setChanged(true)}
  onChange() {this.setChanged(true)}

  async exec(eve) {
    if (await super.exec(eve)) {
      this.setChanged(false)
      for (const elem of this.elements) elem.onFormSubmitted?.()
      return true
    }
    return false
  }

  // Used by `MixExecable`.
  async onErr(err) {
    const msg = this.lastFormMsg()
    if (msg) return msg.setErr(err)
    return super.onErr(err)
  }

  // Used by `MixExecable`.
  // Override in subclass.
  perform() {throw u.errImplementInSubclass()}
  firstBtnSubmit() {return a.find(this.elements, u.isSubmitBtn)}
  lastFormMsg() {return a.last(this.descs(FormMsg))}
  isPending() {return this.getCounter() > 0}
  isDone() {return this.getObs()?.isDone()}
  isChanged() {return this.hasAttribute(`data-changed`)}

  setChanged(next) {
    a.reqBool(next)
    const prev = this.isChanged()
    this.toggleAttribute(`data-changed`, next)

    if (prev !== next) {
      for (const elem of this.elements) elem.onFormChanged?.(next)
    }
  }

  // Called by `MixExecable..setCounter`.
  setDisabled(next) {
    a.reqBool(next)
    for (const elem of this.elements) elem.onFormDisabled?.(next)
  }

  /*
  TODO: instead of iterating `.elements`, consider walking descendants in the
  tree order (pre-order, depth-first), to avoid deep-walking any descendants
  which implement our `.addFormValue` method. This would allow safe nesting of
  form fields, for cases when a higher-level form field includes multiple
  lower-level form fields.
  */
  formData() {
    const tar = a.Emp()
    for (const val of this.elements) val.addFormValue?.(tar)
    return tar
  }
}

/*
Tentative replacement for React-based forms. Currently supports only a limited
set of input types. All inputs are implemented as custom elements. Supports
server-generated `UiForm` and `UiField` specs as-is without special conversion.
Does NOT use special observables. The input state is stored only in the inputs
themselves, using native DOM features.
*/
export class SpecForm extends Form {
  static {dr.reg(this)}

  init() {throw u.errImplementInSubclass()}

  initFromSpec(src) {
    return E(this.initSubmit(src), {}, specToFields(this, src))
  }

  initSubmit(src) {
    const {submit} = a.reqInst(src, d.UiForm)
    if (!submit) return this

    /*
    TODO: support this case by using pushstate GET submission, like `PushForm`.
    Requires deduplicating the corresponding code. Might move to superclass.
    */
    if (!submit) throw TypeError(`missing submit info on form spec ${a.show(src)}`)

    this.data().path = submit.path
    this.data().method = submit.method
    return this
  }

  /*
  Submits the form via an HTTP request as described by the form spec. See
  `UiForm`. Assumes that the request must be JSON, and assumes that the form's
  current state will be saved as-is, without the need to inspect the response
  and re-render the form.
  */
  async perform() {
    const {path, method} = this.dataset

    // Placeholder. This should perform a `PushForm`-style GET push.
    if (!path && !method) return undefined

    return a.resOk(this.ctx.fetch({
      url: path,
      method,
      headers: [a.HEADER_JSON],
      body: a.jsonEncode(this.formData()),
    }))
  }
}

export function specToFields(ctx, src) {
  a.reqInst(src, d.UiForm)
  return a.arr(src.fields).map(a.bind(specToField, ctx))
}

// TODO: support more field types.
// TODO: move types to constants. (Also update React-based code.)
export function specToField(ctx, src) {
  src = a.toInst(src, d.UiField)

  if (src.type === c.UI_FIELD_TYPE_BOOL) {
    return new effch.FormFieldBool(ctx).initFromSpec(src)
  }

  if (src.type === c.UI_FIELD_TYPE_TER) {
    return new effch.FormFieldTer(ctx).initFromSpec(src)
  }

  if ((
    false
    || src.type === c.UI_FIELD_TYPE_TEXT
    || src.type === c.UI_FIELD_TYPE_SEARCH
    || src.type === c.UI_FIELD_TYPE_EMAIL
    || src.type === c.UI_FIELD_TYPE_PHONE
  )) {
    return new efft.FormFieldText(ctx).initFromSpec(src)
  }

  if (src.type === c.UI_FIELD_TYPE_CENTS) {
    return new effce.FormFieldCents(ctx).initFromSpec(src)
  }

  if (src.type === c.UI_FIELD_TYPE_SELECT) {
    if (src.multiple) return new effsm.FormFieldSelectMulti(ctx).initFromSpec(src)
    return new effss.FormFieldSelectSingle(ctx).initFromSpec(src)
  }

  u.logDebug(`skipping unrecognized field type ${a.show(src.type)}`)
  return undefined
}

export class PushForm extends Form {
  static {dr.reg(this)}

  debounce = undefined

  connectedCallback() {
    super.connectedCallback()
    this.oninput = this.onActivity
    this.onchange = this.onActivity
    this.addEventListener(`clear`, this)
  }

  disconnectedCallback() {
    this.removeEventListener(`clear`, this)
    this.debounce?.deinit()
  }

  handleEvent(eve) {if (eve.type === `clear`) this.clear(eve)}
  autoSubmit() {return u.parseNum(this.dataset.autoSubmit)}
  onActivity() {this.delay(this.autoSubmit())}

  perform() {
    this.debounce?.deinit()
    this.push(a.query(a.pick(this.formData(), a.truthy)))
  }

  clear(eve) {
    a.eventKill(eve)
    this.push(``)
  }

  push(query) {
    const url = a.url(this.action)
    url.query = query
    this.env.locPush(url)
  }

  delay(delay) {
    if (!delay) return
    if (!this.debounce) this.debounce = new u.Debounce(this)
    this.debounce.delay(delay)
  }

  // Called by `Debounce`.
  debounced() {this.exec()}
}

export class SubForm extends Form {
  static customName = `sub-form`
  static {dr.reg(this)}

  main() {return u.nodeByAttr(this, `data-role`, `main`)}

  async perform() {
    const {ctx} = this
    const body = this.formData()
    const {isNew} = await n.subscribeByEmail(ctx, body)
    ctx.env.gtag.sendEmailSub(body)

    E(this, {},
      isNew
      ? `Done! We will send you the latest news, trends & breakthroughs in health, wellness and spirituality.`
      : `Thanks! You are already subscribed.`
    )
  }
}

export class FormMsg extends u.Elem {
  static customName = `form-msg`
  static {dr.reg(this)}

  clear() {return this.setText(``)}

  setSuc(val) {
    this.setText(val)
    this.reclass(`fg-success`)
    return this
  }

  setErr(err) {
    this.setText(err)
    this.reclass(`fg-error`)
    return this
  }

  setInf(val) {
    this.setText(val)
    this.reclass(`text-italic`)
    return this
  }

  setText(val) {
    const isOpt = `opt` in this.dataset
    super.setText(val || (isOpt ? `` : c.NON_BREAKING_SPACE))
    this.hidden = isOpt && !val
    return this
  }

  reclass(...cls) {
    this.classList.remove(`fg-success`, `fg-error`, `text-italic`)
    this.classList.add(...cls)
    return this
  }
}
